import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { fetchRecipe } from "../lib/api";
import { ArrowLeft, ChefHat, Timer, Flame } from "lucide-react";

const RecipePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchRecipe(id)
            .then(setRecipe)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="h-[100dvh] flex items-center justify-center bg-[#0A0A0A] text-white/60 text-sm">Loading…</div>;
    }
    if (!recipe) {
        return <div className="h-[100dvh] flex items-center justify-center bg-[#0A0A0A] text-white/60 text-sm">Not found.</div>;
    }

    const macros = [
        { label: "Calories", value: `${recipe.calories}`, unit: "kcal" },
        { label: "Protein", value: `${recipe.protein_g}`, unit: "g" },
        { label: "Carbs", value: `${recipe.carbs_g}`, unit: "g" },
        { label: "Fat", value: `${recipe.fat_g}`, unit: "g" },
    ];

    return (
        <div data-testid="recipe-page" className="min-h-[100dvh] bg-[#0A0A0A] pb-32">
            <div className="relative h-[50dvh]">
                <img
                    src={recipe.image_url}
                    alt={recipe.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-[#0A0A0A]/40" />
                <button
                    data-testid="back-button"
                    onClick={() => navigate(-1)}
                    className="absolute top-5 left-4 w-10 h-10 rounded-full bg-black/50 border border-white/10 backdrop-blur flex items-center justify-center"
                >
                    <ArrowLeft size={18} />
                </button>
                <span className="absolute top-5 right-4 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-white text-[#0A0A0A] flex items-center gap-1">
                    <ChefHat size={11} /> Recipe
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <img
                            src={recipe.chef_avatar}
                            alt={recipe.chef}
                            className="w-8 h-8 rounded-full object-cover border border-white/30"
                        />
                        <span className="text-sm font-semibold">{recipe.chef}</span>
                        <span className="text-xs text-white/50 flex items-center gap-1 ml-auto">
                            <Timer size={11} /> {recipe.prep_min} min
                        </span>
                    </div>
                    <h1 className="font-display text-5xl uppercase leading-[0.95]">{recipe.title}</h1>
                </div>
            </div>

            <div className="px-5 pt-6 space-y-7">
                <p className="text-sm text-white/80 leading-relaxed">{recipe.summary}</p>

                {recipe.workout_id && (
                    <Link
                        to={`/workout/${recipe.workout_id}`}
                        data-testid="paired-workout-link"
                        className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#E1FF01]/10 border border-[#E1FF01]/40 text-[#E1FF01] text-xs uppercase tracking-widest font-bold w-fit"
                    >
                        <Flame size={12} />
                        Paired with workout subscription →
                    </Link>
                )}

                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-3">Macros</p>
                    <div className="grid grid-cols-4 gap-2">
                        {macros.map((m) => (
                            <div
                                key={m.label}
                                className="bg-[#141414] border border-neutral-800 rounded-xl p-3 text-center"
                            >
                                <p className="font-display text-2xl">{m.value}</p>
                                <p className="text-[10px] uppercase tracking-widest text-white/50">
                                    {m.unit} · {m.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-3">Ingredients</p>
                    <ul className="space-y-1.5">
                        {recipe.ingredients?.map((line, i) => (
                            <li
                                key={i}
                                className="text-sm text-white/85 flex items-start gap-2 border-b border-neutral-900 pb-1.5"
                            >
                                <span className="text-[#E1FF01] font-bold mt-0.5">·</span>
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-3">Steps</p>
                    <ol className="space-y-3">
                        {recipe.steps?.map((s, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="font-display text-3xl text-[#E1FF01] leading-none w-8 shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="text-sm text-white/85 leading-relaxed pt-1">{s}</p>
                            </li>
                        ))}
                    </ol>
                </div>

                <p className="text-[10px] text-center text-white/30 uppercase tracking-widest pt-4">
                    Recipes are suggestions only · No meals shipped
                </p>
            </div>
        </div>
    );
};

export default RecipePage;
